/**
 * Org-scoped email variable store.
 *
 * Holds reusable merge tokens (e.g. {{location}} = "San Francisco, CA") that
 * the recruiter can inject into any email template. Backed by localStorage
 * for now — schema is deliberately small so a future Supabase migration
 * (e.g. `ggj_b2b_email_variables` table) can drop in without touching the UI.
 *
 * All reads/writes are namespaced per `orgId`, so variables don't leak
 * across multi-tenant sessions in the same browser.
 */

export interface EmailVariable {
  id: string;
  name: string;
  value: string;
  updatedAt: string;
}

const KEY_PREFIX = 'ggj.email.variables.';

function storageKey(orgId: string): string {
  return `${KEY_PREFIX}${orgId}`;
}

function randomId(): string {
  return `var_${Math.random().toString(36).slice(2, 10)}`;
}

/** Normalises names to uppercase snake_case so merge tokens stay predictable. */
export function normaliseVarName(raw: string): string {
  return raw
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^A-Za-z0-9_]/g, '')
    .toUpperCase();
}

export function listEmailVariables(orgId: string): EmailVariable[] {
  if (!orgId || typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(storageKey(orgId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as EmailVariable[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeAll(orgId: string, vars: EmailVariable[]): void {
  try {
    localStorage.setItem(storageKey(orgId), JSON.stringify(vars));
    // Allow other tabs / panels to react.
    window.dispatchEvent(
      new CustomEvent('ggj:email-variables-changed', { detail: { orgId } }),
    );
  } catch {
    /* quota / private mode — silently drop */
  }
}

export function addEmailVariable(
  orgId: string,
  input: { name: string; value: string },
): EmailVariable {
  const now = new Date().toISOString();
  const v: EmailVariable = {
    id: randomId(),
    name: normaliseVarName(input.name),
    value: input.value,
    updatedAt: now,
  };
  const next = [...listEmailVariables(orgId), v];
  writeAll(orgId, next);
  return v;
}

export function updateEmailVariable(
  orgId: string,
  id: string,
  patch: Partial<Pick<EmailVariable, 'name' | 'value'>>,
): void {
  const next = listEmailVariables(orgId).map((v) =>
    v.id === id
      ? {
          ...v,
          name: patch.name != null ? normaliseVarName(patch.name) : v.name,
          value: patch.value != null ? patch.value : v.value,
          updatedAt: new Date().toISOString(),
        }
      : v,
  );
  writeAll(orgId, next);
}

export function deleteEmailVariable(orgId: string, id: string): void {
  const next = listEmailVariables(orgId).filter((v) => v.id !== id);
  writeAll(orgId, next);
}

/** Useful helper for the template editor's "insert variable" dropdown. */
export function getDefaultVariables(): { name: string; hint: string }[] {
  return [
    { name: 'FIRST_NAME', hint: 'Candidate first name' },
    { name: 'LAST_NAME', hint: 'Candidate last name' },
    { name: 'COMPANY', hint: 'Candidate current company' },
    { name: 'JOB_TITLE', hint: 'Role being offered' },
    { name: 'CLIENT_NAME', hint: 'Hiring client' },
    { name: 'RECRUITER_NAME', hint: 'Sender name' },
  ];
}
